import { lessonsTarget } from '../utils/openInLessons';
import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { DOUBLE_BAGEL_UID } from '../constants';
import { useLessons } from '../contexts/LessonContext';
import MessageBubble from './MessageBubble';
const VideoEditorModal = lazy(() => import('./VideoEditorModal').then(m => ({ default: m.VideoEditorModal })));
import { db } from '../firebase';
import {
  subscribeToConversation,
  sendMessage,
  sendVideoMessage,
  sendDrillAttachmentMessage,
  sendProVideoAttachmentMessage,
  editMessage,
  deleteMessage,
  setMessageReaction,
  markMessagesAsRead,
  setTypingState,
} from '../services/chat';
import { DrillAttachmentPickerModal } from './DrillAttachmentPickerModal';
import type { Drill, ProVideo } from '../types/lesson';
import { subscribeToLessonsForConversation } from '../services/lessons';
import { getUserProfile } from '../services/userProfile';
import { CalendarPlus, Clock } from '@phosphor-icons/react';
import { AdjustTimeSheet } from './live/AdjustTimeSheet';
import { RequestNewTime } from './live/RequestNewTime';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_SECONDARY_SM } from './buttonStyles';
import Sheet from './Sheet';
import { lessonChip } from '../utils/lessonChip';
import { liveLessonsErrorMessage } from '../services/liveLessons';
import { answerLiveLessonHappened, subscribeNextLessonWith } from '../services/liveV2';
import { CoachRequestTimesOverlay } from './live/CoachRequestTimes';
import { firstNameOf } from '../utils/liveLessonText';
import { subscribePairBookings } from '../services/liveCallBookings';
import { PairBookingsContext } from '../contexts/PairBookingsContext';
import { finishedThreadLine } from '../utils/deliveredText';
import { useViewerZone } from '../hooks/useViewerZone';
import { timeLabel, whenLine } from '../utils/liveCalendar';
import type { LiveCallBooking } from '../types/liveCall';
import { TimeCardComposer } from './live/TimeCardComposer';
import { useMembershipWith } from '../hooks/useMembershipWith';
import { groupMessagesIntoSessions } from '../utils/messageSessions';
import { coachProfilePath } from '../utils/coachNav';
import { getOrdersForConversation } from '../services/orders';
import { pickBannerProgram } from '../utils/bannerState';
import PlayerProfileModal from './PlayerProfileModal';
import type { Conversation, Message, Lesson, ReactionKey } from '../types/chat';
import { MESSAGE_EDIT_WINDOW_MINUTES } from '../types/chat';
import type { Order } from '../types/order';
import type { UserProfile } from '../types/user';
import UploadNextLessonModal from './UploadNextLessonModal';
import { useToast } from '../hooks/useToast';
import { VV_CHANGE_EVENT } from '../hooks/useVisualViewportVars';
import { LogoSvg } from './Logo';
import { chatParticipantName } from '../utils/participantName';
import { Avatar } from './Avatar';

interface ConversationViewProps {
  conversationId: string;
  initialLessonId?: string; // If provided, auto-open Order Details panel for this lesson
  initialLessonKey?: number; // Change this to re-trigger opening for the same lesson
  hideHeader?: boolean; // Hide the header when embedded in another view (e.g., mobile Inbox)
  /** Mobile back handler. When supplied for a training-program conversation,
   *  the merged ProgramConversationHeader owns top-bar navigation — Inbox.tsx
   *  suppresses its own mobile chat header in this case to avoid a double-header. */
  onMobileBack?: () => void;
  senderOverride?: string; // When set, messages are sent/displayed as this uid instead of currentUser
  /** Hides the message composer and suppresses Reply — for admin read-only views. */
  readOnly?: boolean;
  /** Override the default lesson-open behaviour (e.g. admin shows a modal instead of navigating). */
  onOpenLesson?: (lesson: Lesson) => void;
}

export default function ConversationView({ conversationId, initialLessonId, initialLessonKey, hideHeader = false, senderOverride, readOnly = false, onOpenLesson: onOpenLessonProp }: ConversationViewProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  // effectiveUid: use senderOverride when staff are posting as Double Bagel
  const effectiveUid = senderOverride ?? currentUser?.uid;
  const { getLessonsForConversation } = useLessons();
  const { showSuccess, showError } = useToast();

  const [messages, setMessages] = useState<Message[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const isDoubleBagelChat = currentConversation?.participants.includes(DOUBLE_BAGEL_UID) ?? false;
  // A draft handed over by the link that opened the thread (a coach's
  // Follow Up from a Delivered call), left for them to edit and send.
  const location = useLocation();
  const [messageText, setMessageText] = useState(
    () => (location.state as { draft?: string } | null)?.draft ?? '',
  );
  const [sending, setSending] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showDrillPickerModal, setShowDrillPickerModal] = useState(false);
  // OTHER participant's profile — authoritative source for avatar shape
  // (role === 'coach' → rectangle, otherwise circle). Replaces prior
  // lessons/Conversation.coachId inference that misfired on threads
  // without lessons or with an unpopulated coachId field.
  const [otherUserProfile, setOtherUserProfile] = useState<UserProfile | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [editingVideo, setEditingVideo] = useState<Message | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [deletingMessage, setDeletingMessage] = useState<Message | null>(null);

  // ── Edit / delete permission helpers ───────────────────────────
  // If a message being used as a reply reference is later edited, reflect
  // the current text in the reply preview without any extra Firestore writes.
  // The live `messages` state (from the onSnapshot subscription) is the
  // authoritative source; this just reads from it at render time.
  const withLiveReplyText = (msg: Message): Message => {
    if (!msg.replyToMessageId) return msg;
    const original = messages.find(m => m.id === msg.replyToMessageId);
    if (!original || original.isDeleted || original.text === msg.replyToText) return msg;
    return { ...msg, replyToText: original.text };
  };

  const canEditMsg = (msg: Message): boolean => {
    if (msg.isDeleted) return false;
    if (msg.type !== 'text') return false;
    if (isAdmin) return true;
    if (!effectiveUid || msg.senderId !== effectiveUid) return false;
    const ageMs = Date.now() - new Date(msg.createdAt).getTime();
    return ageMs <= MESSAGE_EDIT_WINDOW_MINUTES * 60_000;
  };

  const canDeleteMsg = (msg: Message): boolean => {
    if (msg.isDeleted) return false;
    if (msg.type === 'system') return false;
    if (isAdmin) return true;
    if (!effectiveUid || msg.senderId !== effectiveUid) return false;
    // Same one-hour window as editing. Delete previously had none at all.
    const ageMs = Date.now() - new Date(msg.createdAt).getTime();
    if (ageMs > MESSAGE_EDIT_WINDOW_MINUTES * 60_000) return false;
    // And not once the other person has answered. `messages` is the thread in
    // chronological order, so "answered" is any later message from a different
    // sender. Deleting after a reply leaves the reply pointing at a tombstone,
    // which reads as the other person talking to themselves.
    const sentAt = new Date(msg.createdAt).getTime();
    const answered = messages.some(m => (
      m.senderId !== msg.senderId
      && !m.isDeleted
      && new Date(m.createdAt).getTime() > sentAt
    ));
    return !answered;
  };

  // ── Edit handlers ───────────────────────────────────────────────
  const handleEditMessage = (msg: Message) => {
    setEditingMessageId(msg.id);
  };

  const handleEditSave = async (msg: Message, newText: string) => {
    if (!newText || !effectiveUid || newText === msg.text) {
      setEditingMessageId(null);
      return;
    }
    try {
      await editMessage(
        conversationId,
        msg.id,
        newText,
        effectiveUid,
        chatParticipantName(userProfile),
        msg.text ?? '',
      );
      setEditingMessageId(null);
    } catch (err) {
      console.error('Failed to edit message:', err);
      showError('Failed to save edit');
    }
  };

  const handleEditCancel = () => setEditingMessageId(null);

  // ── Delete handlers ─────────────────────────────────────────────
  /**
   * iMessage's swipe-to-reveal. Dragging the thread left slides a timestamp
   * column in from the right edge; releasing springs it back.
   *
   * Axis locking is what keeps this from fighting the two gestures already on
   * these bubbles:
   *
   *   Vertical scrolling — `touch-action: pan-y` on the list hands vertical
   *   panning to the browser outright, so the scroll never goes through React
   *   and never stutters. The first move past the threshold locks the gesture
   *   to one axis for its whole duration; once locked to 'y' this handler does
   *   nothing at all until the finger lifts.
   *
   *   Long-press — the bubble's own hook cancels at 10px of movement. The
   *   engage threshold here is deliberately 12, above that, so any drag that
   *   reveals a timestamp has already killed the pending long press. The two
   *   can never both fire.
   *
   * Only leftward drags do anything: `revealX` is clamped to 0 at the bottom,
   * so dragging right is inert rather than pushing the thread off-screen.
   */
  const [revealX, setRevealX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const dragAxis = useRef<'x' | 'y' | null>(null);

  const handleRevealTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    dragOrigin.current = { x: t.clientX, y: t.clientY };
    dragAxis.current = null;
  };

  const handleRevealTouchMove = (e: React.TouchEvent) => {
    const o = dragOrigin.current;
    const t = e.touches[0];
    if (!o || !t) return;
    const dx = t.clientX - o.x;
    const dy = t.clientY - o.y;

    if (dragAxis.current === null) {
      if (Math.abs(dx) < REVEAL_ENGAGE_PX && Math.abs(dy) < REVEAL_ENGAGE_PX) return;
      dragAxis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (dragAxis.current === 'x') setDragging(true);
    }
    if (dragAxis.current !== 'x') return;

    // Rubber-band past the maximum rather than stopping dead.
    const pulled = Math.max(0, -dx - REVEAL_ENGAGE_PX);
    setRevealX(pulled <= REVEAL_MAX_PX ? pulled : REVEAL_MAX_PX + (pulled - REVEAL_MAX_PX) * 0.2);
  };

  const handleRevealTouchEnd = () => {
    dragOrigin.current = null;
    dragAxis.current = null;
    setDragging(false);
    setRevealX(0);
  };

  /**
   * Toggling a Tapback. The write is a single dotted-path field update, so
   * there is nothing to reconcile locally — the snapshot listener already
   * driving this thread brings the change back within a frame or two, and a
   * failure simply means the pill never appears.
   */
  const handleReact = async (msg: Message, reaction: ReactionKey | null) => {
    if (!effectiveUid) return;
    try {
      await setMessageReaction(conversationId, msg.id, effectiveUid, reaction);
    } catch (err) {
      console.error('Failed to set reaction:', err);
      showError('Could not save your reaction');
    }
  };

  const handleDeleteMessage = (msg: Message) => setDeletingMessage(msg);

  const handleConfirmDelete = async () => {
    if (!deletingMessage || !effectiveUid) return;
    try {
      await deleteMessage(conversationId, deletingMessage.id, effectiveUid);
      setDeletingMessage(null);
    } catch (err) {
      console.error('Failed to delete message:', err);
      showError('Failed to delete message');
    }
  };

  // ── Typing indicator state ──────────────────────────────────────
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  const emitTyping = () => {
    if (!conversationId || !effectiveUid) return;
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      setTypingState(conversationId, effectiveUid, true).catch(console.error);
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      setTypingState(conversationId, effectiveUid, false).catch(console.error);
    }, 3000);
  };

  const stopTyping = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (isTypingRef.current) {
      isTypingRef.current = false;
      if (conversationId && effectiveUid) {
        setTypingState(conversationId, effectiveUid, false).catch(console.error);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (isTypingRef.current && conversationId && effectiveUid) {
        setTypingState(conversationId, effectiveUid, false).catch(console.error);
      }
    };
  }, [conversationId, effectiveUid]);

  const otherIsTyping = useMemo(() => {
    if (!currentConversation?.typing || !effectiveUid) return false;
    const otherUserId = currentConversation.participants.find(id => id !== effectiveUid);
    if (!otherUserId) return false;
    const typingTimestamp = currentConversation.typing[otherUserId];
    if (!typingTimestamp) return false;
    if (typeof typingTimestamp === 'object' && typingTimestamp !== null && 'toDate' in typingTimestamp) {
      const typingDate = (typingTimestamp as { toDate: () => Date }).toDate();
      return (Date.now() - typingDate.getTime()) < 10000;
    }
    if (typeof typingTimestamp === 'string') {
      return (Date.now() - new Date(typingTimestamp).getTime()) < 10000;
    }
    return true;
  }, [currentConversation?.typing, effectiveUid, currentConversation?.participants]);

  // Coach-only peek at the player's basic profile. Opened from the
  // header avatar/name and from player message bubble avatars. State
  // lives here so one modal instance services every click site in
  // the thread.
  const [playerProfileModal, setPlayerProfileModal] = useState<
    { playerId: string; seedName?: string; seedAvatarUrl?: string | null } | null
  >(null);
  const contextLessons = useMemo(() => getLessonsForConversation(conversationId), [getLessonsForConversation, conversationId]);

  // When readOnly (admin Chats view), the LessonContext only holds the
  // viewer's own lessons — admin has none, so contextLessons is always [].
  // Subscribe directly to Firestore instead so lesson cards can render
  // playerMessage (player note) and coachNotes correctly.
  const [directLessons, setDirectLessons] = useState<Lesson[]>([]);
  useEffect(() => {
    if (!readOnly || !conversationId) { setDirectLessons([]); return; }
    return subscribeToLessonsForConversation(conversationId, setDirectLessons);
  }, [readOnly, conversationId]);

  const lessons = useMemo(
    () => readOnly ? directLessons : contextLessons,
    [readOnly, directLessons, contextLessons],
  );
  const lastProcessedInitialLessonKeyRef = useRef<number | undefined>(undefined);

  // Decision 108: the header carries the next call alone; no video-call button, no three-dot menu (it held
  // View last lesson, Leave a review and Booking options) and no Order Details drawer. Inbox.tsx still calls
  // useConversationLessonAction for the mobile chat header, so that hook stays.

  // Reply index. Built once per snapshot rather than searched per bubble:
  // subscribeToConversation has no limit(), so the whole thread is already in
  // state and both lookups are pure memory. No query, and the parent is always
  // present even when it has scrolled far out of frame.
  //
  // Children are counted one level deep — `replyToMessageId` points one step up
  // and there is no thread-root field, so a reply to a reply belongs to its own
  // parent, not to the grandparent.
  const { replyChildCount, messageById } = useMemo(() => {
    const counts = new Map<string, number>();
    const byId = new Map<string, Message>();
    for (const m of messages) {
      byId.set(m.id, m);
      if (m.replyToMessageId && !m.isDeleted) {
        counts.set(m.replyToMessageId, (counts.get(m.replyToMessageId) ?? 0) + 1);
      }
    }
    return { replyChildCount: counts, messageById: byId };
  }, [messages]);

  // ── Focus mode ──────────────────────────────────────────────────────
  // The reply thread, shown sharp over a blurred copy of the conversation.
  // Holds a message id rather than the message so it survives a snapshot.
  // How much of THIS component the on-screen keyboard is covering.
  //
  // The keyboard does not change window.innerHeight on iOS; it slides over the
  // page. On mobile Inbox the pane is a vv-shell that already stops at the
  // keyboard, so the answer there is 0 and the composer sits at the pane's own
  // bottom. The measurement is against this component's box rather than the
  // window so that any host that does not shrink (admin Chats and Support,
  // opened on a phone) still gets its composer lifted clear of the keyboard.
  //
  // Driven by VV_CHANGE_EVENT, not by visualViewport directly: that event
  // fires after the shell has been resized, and a direct listener here would
  // run first (child effects run before App's) and measure the old height.
  const rootRef = useRef<HTMLDivElement>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      const root = rootRef.current;
      if (!root || Math.abs(vv.scale - 1) > 0.01) { setKeyboardInset(0); return; }
      const visibleBottom = vv.offsetTop + vv.height;
      setKeyboardInset(Math.max(0, Math.round(root.getBoundingClientRect().bottom - visibleBottom)));
    };
    window.addEventListener(VV_CHANGE_EVENT, sync);
    return () => window.removeEventListener(VV_CHANGE_EVENT, sync);
  }, []);

  const [focusedId, setFocusedId] = useState<string | null>(null);

  const focusedThread = useMemo(() => {
    if (!focusedId) return null;
    const parent = messageById.get(focusedId);
    if (!parent) return null;
    const replies = messages
      .filter(m => m.replyToMessageId === focusedId && !m.isDeleted)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return { parent, replies };
  }, [focusedId, messageById, messages]);

  // Entering sets the reply target too, so the composer is already aimed at
  // the parent and sending needs no extra step.
  const openThread = (messageId: string) => {
    const target = messageById.get(messageId);
    if (!target) return;
    setFocusedId(messageId);
    setReplyingTo(target);
    inputRef.current?.focus();
  };

  const closeThread = () => {
    setFocusedId(null);
    setReplyingTo(null);
  };

  useEffect(() => {
    if (!focusedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeThread(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focusedId]);

  // A focused thread whose parent has gone (deleted, or never loaded) has
  // nothing to show, so drop out rather than render an empty scrim. Both bits
  // of state have to go: clearing only focusedId would leave the composer
  // silently aimed at a message that is no longer on screen, and with the
  // "Replying to" bar gone there would be nothing to show for it.
  useEffect(() => {
    if (focusedId && !messageById.has(focusedId)) {
      setFocusedId(null);
      setReplyingTo(null);
    }
  }, [focusedId, messageById]);


  // Banner: active order for player-facing training program progress banner.
  // We also keep all orders in this conversation so each MessageBubble can be
  // labelled with the right offering type without an extra per-bubble fetch.
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [conversationOrders, setConversationOrders] = useState<Order[]>([]);

  // Live identity lookups for the counterparty rendered in the program
  // banner / chat header. Snapshot fields on activeOrder (coachName,
  // coachAvatarUrl, playerName, playerAvatarUrl) are write-once at order
  // creation and don't refresh after a rename — the live values below
  // override the snapshot so the header shows the user's current name +
  // photo. Null while loading; the snapshot fallback covers first paint.
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Set by the banner CTA dispatcher when the upload target lesson
  // number is known (e.g. from cta.lessonNumber). Drives the upload
  // modal's `nextLessonNum` directly so the watch-pending case (no
  // reserved lesson doc yet) can still open the modal scoped to N+1.
  const [pendingUploadLessonNumber, setPendingUploadLessonNumber] = useState<number | null>(null);

  /* Already computed inline inside handleOpenLesson; lifted so the thread menu
     can use the same answer rather than a second definition of "is a coach". */
  const viewerIsCoach = useMemo(
    () => !!effectiveUid && lessons.some(l => l.coachId === effectiveUid),
    [effectiveUid, lessons],
  );

  const handleOpenLesson = (lesson: Lesson) => {
    // No Order Details drawer (Ryan, 29 Oct): the lesson opens on the coach's Orders or the player's Lessons.
    navigate(viewerIsCoach ? `/orders?open=${lesson.id}` : `/lessons?open=${lesson.id}`);
  };



  /**
   * One bubble, rendered identically in the thread and inside focus mode.
   * Extracted so the two cannot drift: focus mode shows real bubbles, with
   * working reactions and menus, not a read-only copy.
   */
  const renderMessageBubble = (
    message: Message,
    opts: {
      showAvatar: boolean;
      messageLesson: Lesson | null;
      messageOrder: Order | null;
      /** Focus mode renders plain bubbles: no ghost, no hook, no reply count. */
      hideReplyChrome?: boolean;
    },
  ) => {
    const { showAvatar, messageLesson, messageOrder, hideReplyChrome = false } = opts;
    return (
    <MessageBubble
      message={message.type === 'system' && message.liveEvent === 'delivered_ready'
        ? { ...message, text: finishedThreadLine(message.createdAt, pairBookings) }
        : withLiveReplyText(message)}
      isOwnMessage={message.senderId === effectiveUid}
      isDoubleBagelSender={message.senderId === DOUBLE_BAGEL_UID}
      showStaffAttribution={!!senderOverride}
      lesson={messageLesson ?? null}
      lessonOrder={messageOrder}
      isCoachSender={!!lessons.find(l => l.coachId === message.senderId)}
      showAvatar={showAvatar}
      hideReplyChrome={hideReplyChrome}
      replyCount={replyChildCount.get(message.id) ?? 0}
      replyParent={message.replyToMessageId
        ? messageById.get(message.replyToMessageId) ?? null
        : null}
      onReply={readOnly ? undefined : handleReply}
      onOpenThread={openThread}
      onReplyWithVideoEdit={handleReplyWithVideoEdit}
      onOpenLesson={onOpenLessonProp ?? handleOpenLesson}
      canEdit={canEditMsg(message)}
      canDelete={canDeleteMsg(message)}
      currentUserId={effectiveUid ?? undefined}
      onReact={handleReact}
      isEditing={editingMessageId === message.id}
      onEdit={handleEditMessage}
      onDelete={handleDeleteMessage}
      onEditSave={(newText) => handleEditSave(message, newText)}
      onEditCancel={handleEditCancel}
      onAvatarClick={(() => {
        if (message.senderId === effectiveUid) return undefined;
        const viewerIsCoach = !!effectiveUid && lessons.some(l => l.coachId === effectiveUid);
        const senderIsCoach = lessons.some(l => l.coachId === message.senderId);
        // Player viewer tapping a coach avatar → coach profile (existing behavior).
        if (!viewerIsCoach && senderIsCoach) {
          return () => navigate(coachProfilePath(message.senderId));
        }
        // Coach viewer tapping a player avatar → peek modal.
        if (viewerIsCoach && !senderIsCoach) {
          return () => setPlayerProfileModal({
            playerId: message.senderId,
            seedName: message.senderName,
            seedAvatarUrl: message.senderAvatarUrl,
          });
        }
        return undefined;
      })()}
    />
    );
  };

  // Auto-open panel if initialLessonId is provided (coach only; players go to /lessons)
  useEffect(() => {
    if (initialLessonId && lessons.length > 0 && lastProcessedInitialLessonKeyRef.current !== initialLessonKey) {
      lastProcessedInitialLessonKeyRef.current = initialLessonKey;
      const isCoach = effectiveUid && lessons.some(l => l.coachId === effectiveUid);
      navigate(isCoach ? `/orders?open=${initialLessonId}` : `/lessons?open=${initialLessonId}`);
    }
  }, [initialLessonId, initialLessonKey, lessons, effectiveUid, navigate]);

  // Resolve all orders attached to this conversation. We query by
  // conversationId directly — NOT via the lessons array's orderId — so
  // training-program orders whose lesson docs haven't been created yet
  // (lazy lesson creation; auto-renew orders) still surface. That's the
  // root cause of the player-side banner-missing bug: before lesson #1
  // exists, lesson.orderId fan-out returns nothing, and pickBannerProgram
  // never sees the program.
  useEffect(() => {
    if (!conversationId) {
      setActiveOrder(null);
      setConversationOrders([]);
      return;
    }
    let cancelled = false;
    getOrdersForConversation(conversationId)
      .then(orders => {
        if (cancelled) return;
        setConversationOrders(orders);
        setActiveOrder(pickBannerProgram(orders, lessons));
      })
      .catch(() => {
        if (cancelled) return;
        setConversationOrders([]);
        setActiveOrder(null);
      });
    return () => { cancelled = true; };
  }, [conversationId, lessons]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view when the list changes height. Opening the
  // keyboard, closing it, or switching to the emoji panel all resize the pane
  // (a vv-shell) and with it this list; without this the latest messages would
  // end up under the keyboard. Only when the reader was already at the bottom:
  // someone scrolled back through history keeps their place.
  //
  // A ResizeObserver rather than a keyboard signal, so it covers every cause of
  // a resize, and never fires on desktop where the list does not change height.
  const hasConversation = !!currentConversation;
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const NEAR_BOTTOM_PX = 80;
    let atBottom = true;
    let lastHeight = el.clientHeight;
    const onScroll = () => {
      atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    };
    const ro = new ResizeObserver(() => {
      if (el.clientHeight === lastHeight) return;
      lastHeight = el.clientHeight;
      if (atBottom) el.scrollTop = el.scrollHeight;
    });
    el.addEventListener('scroll', onScroll, { passive: true });
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [hasConversation]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  // Tracks whether we've already scrolled to a ?message= anchor so it only fires once per navigation
  const anchorScrolledRef = useRef(false);

  // Load user profile (for the effective sender)
  useEffect(() => {
    if (!effectiveUid) return;
    getUserProfile(effectiveUid).then((profile) => {
      setUserProfile(profile);
    });
  }, [effectiveUid]);

  // Load the OTHER participant's profile so the header avatar can pick
  // its shape from profile.role. Skips DoubleBagel — that thread has
  // its own dedicated header branch and stays circular regardless.
  useEffect(() => {
    if (!currentConversation || !effectiveUid) { setOtherUserProfile(null); return; }
    const otherUid = currentConversation.participants.find(id => id !== effectiveUid);
    if (!otherUid || otherUid === DOUBLE_BAGEL_UID) { setOtherUserProfile(null); return; }
    let cancelled = false;
    getUserProfile(otherUid).then(profile => {
      if (!cancelled) setOtherUserProfile(profile);
    }).catch(() => { if (!cancelled) setOtherUserProfile(null); });
    return () => { cancelled = true; };
  }, [currentConversation, effectiveUid]);

  // Subscribe to conversation messages and details
  useEffect(() => {
    if (!conversationId || !effectiveUid) {
      setMessages([]);
      setCurrentConversation(null);
      return;
    }

    // Subscribe to conversation for real-time updates
    const conversationUnsubscribe = subscribeToConversation(conversationId, (updatedMessages) => {
      /* A review does not appear in the thread at all — no card, no system
         line. Filtered here rather than by returning null from MessageBubble,
         so session grouping never sees it either and a review cannot leave an
         empty session header behind.
         The message document is still written by addLessonReview and is left
         alone: the conversation list's "Review left · 5 stars" preview matches
         on the conversation's lastMessage TEXT, not on this type, so it is
         unaffected by the thread hiding it. */
      setMessages(updatedMessages.filter((m) => m.type !== 'lesson_review_submitted'));
    });

    // Subscribe to conversation details for status updates
    const conversationDoc = doc(db, 'conversations', conversationId);
    const detailsUnsubscribe = onSnapshot(conversationDoc, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const conv = {
          id: snapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
          lastMessageAt: data.lastMessageAt?.toDate?.().toISOString(),
          updatedAt: data.updatedAt?.toDate?.().toISOString() || new Date().toISOString(),
        } as Conversation;

        setCurrentConversation(conv);
      }
    });

    // Skip read-receipt tracking when senderOverride is set (admin support view):
    // the staff member's UID is not a participant in Double Bagel conversations,
    // so Firestore would reject the unreadCount update.
    if (currentUser?.uid && !senderOverride) {
      markMessagesAsRead(conversationId, currentUser.uid);
    }

    return () => {
      conversationUnsubscribe();
      detailsUnsubscribe();
    };
  }, [conversationId, effectiveUid]);

  // Scroll to a specific message anchor (?message=) on first load, otherwise scroll to bottom
  useEffect(() => {
    if (messages.length === 0) return;
    const anchorId = searchParams.get('message');
    if (anchorId && !anchorScrolledRef.current) {
      const el = messageRefs.current[anchorId];
      if (el) {
        anchorScrolledRef.current = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // If element not yet in refs (messages still loading), keep waiting
    } else if (!anchorId) {
      // Scoped to the thread's own scroll container rather than
      // scrollIntoView, which scrolls EVERY scrollable ancestor including the
      // document — on desktop that dragged the whole page down past the top
      // bar and the Messages heading on first load.
      const el = messagesScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages, searchParams]);

  const handleSendMessage = async () => {
    if (!messageText.trim() || !conversationId || !effectiveUid || sending) return;

    setSending(true);
    try {
      await sendMessage(
        conversationId,
        effectiveUid,
        messageText.trim(),
        'text',
        replyingTo ? {
          replyToMessageId: replyingTo.id,
          replyToText: replyingTo.text || (replyingTo.type === 'video' ? 'Video' : ''),
          replyToSenderName: replyingTo.senderName,
          replyToType: replyingTo.type,
        } : undefined,
        undefined,
        senderOverride && currentUser ? { id: currentUser.uid, name: currentUser.displayName || currentUser.email || 'Staff' } : undefined
      );
      setMessageText('');
      // Reset the auto-grown height back to single-line after a multi-line send.
      if (inputRef.current) inputRef.current.style.height = 'auto';
      stopTyping();
      // Focus mode keeps its reply target: the next message is a reply too,
      // which is what "sending keeps you in focus mode" means for the composer.
      if (!focusedId) setReplyingTo(null);
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Reply from the context menu opens the same focus mode the "N Replies"
  // label does, rather than only arming the composer.
  const handleReply = (message: Message) => {
    openThread(message.id);
  };




  const handleReplyWithVideoEdit = (message: Message) => {
    setEditingVideo(message);
  };

  const handleVideoEditorComplete = async (videoBlob: Blob, notes: string) => {
    if (!conversationId || !effectiveUid || !editingVideo) return;

    try {
      const file = new File([videoBlob], `edited-${Date.now()}.mp4`, { type: 'video/mp4' });

      await sendVideoMessage(
        conversationId,
        effectiveUid,
        file,
        notes || undefined,
        (progress) => setUploadProgress(progress),
        {
          replyToMessageId: editingVideo.id,
          replyToText: editingVideo.text || (editingVideo.type === 'video' ? 'Video' : ''),
          replyToSenderName: editingVideo.senderName,
          replyToType: editingVideo.type,
        }
      );

      setEditingVideo(null);
      showSuccess('Edited video sent successfully!');
    } catch (error) {
      console.error('Error sending edited video:', error);
      showError('Failed to send edited video');
    }
  };

  const handleVideoEditorCancel = () => {
    setEditingVideo(null);
  };

  // Coach in a player conversation gets the drill picker; everyone else goes straight to file browser
  const isCurrentUserCoach = userProfile?.role === 'coach';
  const showDrillPicker = isCurrentUserCoach && !isDoubleBagelChat && otherUserProfile?.role !== 'coach';

  const handleAttachClick = () => {
    if (showDrillPicker) {
      setShowDrillPickerModal(true);
    } else {
      videoInputRef.current?.click();
    }
  };

  const handleVideoSelect = () => {
    videoInputRef.current?.click();
  };

  const handleDrillSelected = async (drill: Drill) => {
    if (!conversationId || !effectiveUid) return;
    try {
      await sendDrillAttachmentMessage(conversationId, effectiveUid, {
        drillId: drill.id,
        drillTitle: drill.title,
        drillDescription: drill.description,
        drillShotType: drill.shotType,
        drillVideoUrl: drill.videoUrl,
        drillThumbnailUrl: drill.thumbnailUrl,
        drillDurationSeconds: drill.durationSeconds,
      });
    } catch (error) {
      console.error('Error sending drill attachment:', error);
      showError('Failed to send drill. Please try again.');
    }
  };

  const handleProVideoSelected = async (video: ProVideo) => {
    if (!conversationId || !effectiveUid) return;
    try {
      await sendProVideoAttachmentMessage(conversationId, effectiveUid, {
        proVideoId: video.id,
        proVideoTitle: video.title,
        proVideoPlayerName: video.playerName,
        proVideoTags: video.tags.length > 0 ? video.tags : undefined,
        proVideoUrl: video.videoUrl,
        proVideoThumbnailUrl: video.thumbnailUrl,
        proVideoDurationSeconds: video.durationSeconds,
      });
    } catch (error) {
      console.error('Error sending pro video attachment:', error);
      showError('Failed to send pro video. Please try again.');
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !conversationId || !effectiveUid) return;

    if (!file.type.startsWith('video/')) {
      showError('Please select a valid video file');
      return;
    }

    const maxSize = 200 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      showError('Video file is too large. Maximum size is 200MB.');
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);

    try {
      await sendVideoMessage(
        conversationId,
        effectiveUid,
        file,
        messageText.trim() || undefined,
        (progress) => {
          setUploadProgress(progress);
        },
        replyingTo ? {
          replyToMessageId: replyingTo.id,
          replyToText: replyingTo.text || (replyingTo.type === 'video' ? 'Video' : ''),
          replyToSenderName: replyingTo.senderName,
          replyToType: replyingTo.type,
        } : undefined
      );
      setMessageText('');
      // Same rule as the text path: focus mode keeps its reply target so the
      // next send is a reply too.
      if (!focusedId) setReplyingTo(null);
      showSuccess('Video sent successfully!');

      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error sending video:', error);
      showError('Failed to send video. Please try again.');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  const getOtherParticipantName = () => {
    if (!currentConversation || !effectiveUid) return 'User';
    const otherParticipantId = currentConversation.participants.find(
      (id) => id !== effectiveUid
    );
    if (!otherParticipantId || !currentConversation.participantDetails) return 'User';
    return currentConversation.participantDetails[otherParticipantId]?.name || 'User';
  };


  // Live lessons v2.0: a Monthly Coaching thread gets Suggest a Time (the
  // coach) or Request a Time (the member) beside the attach button.
  const liveOtherId = currentConversation?.participants.find((id) => id !== effectiveUid) ?? null;
  const { membership: liveMembership, role: liveRole } = useMembershipWith(effectiveUid, liveOtherId);
  const [timeCardOpen, setTimeCardOpen] = useState(false);
  // Adjust Time as a chip in the thread: the next lesson with this person,
  // when it starts within a day (small moves are the last-minute kind).
  const [nextLesson, setNextLesson] = useState<LiveCallBooking | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const viewerZone = useViewerZone();
  useEffect(() => {
    if (!effectiveUid || !liveOtherId || senderOverride) return;
    return subscribeNextLessonWith(effectiveUid, liveOtherId, setNextLesson);
  }, [effectiveUid, liveOtherId, senderOverride]);
  // The thread's finished line, "Lesson finished · 45 min": the pair's lessons, read only when a
  // thread holds one (the server's delivered_ready line names no lesson).
  // The lesson event cards read the same (audit 2 Nov, B14): one listener for the thread, not one per card.
  const hasLessonLines = messages.some((m) => m.type === 'system' && (m.liveEvent === 'delivered_ready' || !!m.bookingId));
  const [pairBookings, setPairBookings] = useState<LiveCallBooking[]>([]);
  const [pairReady, setPairReady] = useState(false);
  useEffect(() => {
    if (!effectiveUid || !liveOtherId || !hasLessonLines) return;
    return subscribePairBookings(effectiveUid, liveOtherId, (bs, ready) => { setPairBookings(bs); setPairReady(ready); });
  }, [effectiveUid, liveOtherId, hasLessonLines]);
  // One action per lesson (audit 2 Nov, T3): only its newest card carries Prep, Join or Open.
  const latestCards = useMemo(() => {
    const out: Record<string, string> = {};
    for (const m of messages) if (m.type === 'system' && m.bookingId) out[m.bookingId] = m.id;
    return out;
  }, [messages]);
  const pair = useMemo(() => (liveOtherId ? { bookings: pairBookings, ready: pairReady, latest: latestCards } : null),
    [liveOtherId, pairBookings, pairReady, latestCards]);
  // The chip changes at the lesson's end: Move until then; after it, for a
  // lesson nobody finished, the coach's "Did this lesson happen?" and
  // nothing for the player (utils/lessonChip).
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const chip = effectiveUid ? lessonChip(nextLesson, effectiveUid, clock) : null;
  const adjustable = chip === 'move';
  const [happenedOpen, setHappenedOpen] = useState(false);
  const [happenedBusy, setHappenedBusy] = useState(false);
  const answerHappened = async (happened: boolean) => {
    if (!nextLesson) return;
    setHappenedBusy(true);
    try {
      await answerLiveLessonHappened({ bookingId: nextLesson.id, happened });
      setHappenedOpen(false);
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not save that. Please try again.'));
    } finally {
      setHappenedBusy(false);
    }
  };

  const getOtherParticipantId = () => {
    if (!currentConversation || !effectiveUid) return null;
    return currentConversation.participants.find(
      (id) => id !== effectiveUid
    ) || null;
  };

  const getOtherParticipantDetails = () => {
    const otherParticipantId = getOtherParticipantId();
    if (!otherParticipantId || !currentConversation || !currentConversation.participantDetails) return null;
    return currentConversation.participantDetails[otherParticipantId] || null;
  };

  if (!currentConversation) {
    return (
      <div className="flex items-center justify-center h-full text-db-text-3">
        <div className="text-center">
          <div className="text-4xl mb-4">💬</div>
          <p className="text-lg font-medium">Select a conversation</p>
          <p className="text-sm mt-2">Choose a conversation from the list to start chatting</p>
        </div>
      </div>
    );
  }


  // Height of the floating identity header: 12 top + 40 avatar + 4 + 18 name
  // line + 12 bottom. The message list reserves this so the first message
  // starts below the block, then scrolls under it.
  // Measured, not assumed: 12 top padding + 40 avatar + 4 gap + 30 pill + 12
  // bottom padding. It was 86, which under-reserved the list's top padding by
  // 10 and, more visibly, left the fade ending well above the pill.
  const FLOATING_HEADER_H = 96;
  // Where the photo and the name pill end: 12 top padding + 40 avatar + 4 gap + 30 pill.
  const HEADER_IDENTITY_H = 86;

/** How far the thread slides left at full reveal, and how much of the row's
 *  right edge the timestamp column therefore occupies. */
const REVEAL_MAX_PX = 72;
/** Movement before the drag commits to an axis. Deliberately above the 10px at
 *  which MessageBubble's long-press cancels, so the two can never both fire. */
const REVEAL_ENGAGE_PX = 12;


/** Space the list reserves under the last message so it clears the composer:
 *  8 + 28 + 8 of composer chrome, plus 16 of air. */
const COMPOSER_CLEARANCE = 60;
  const hasFloatingHeader = !hideHeader;

  return (
    // The list's mask leaves it transparent under the floating header, so what
    // shows there is this root. It carries the page grey; without it the white
    // Inbox pane showed through as a white-to-grey band behind the avatar.
    <PairBookingsContext.Provider value={pair}>
    <div ref={rootRef} className="relative flex flex-col h-full bg-db-bg">
      {/* Chat header — one treatment for every thread. A program used to get a
          saturated banner here instead, carrying its own status, progress
          dashes and a CTA; it is gone and nothing replaced it. A program's
          state lives on the program screen, which is the one place that owns
          it. */}
      {!hideHeader && (() => {
        const isDoubleBagel = currentConversation.participants.includes(DOUBLE_BAGEL_UID);

        if (isDoubleBagel) {
          return (
            /* Floats over the message area: no bar, no border, no fill.
               Support has no profile to open, so this block is not tappable —
               and it carries no chevron, which would promise one. */
            <div className="absolute inset-x-0 top-0 z-10 flex flex-col items-center" style={{ paddingTop: 12, paddingBottom: 12 }}>
              <div className="w-10 h-10 rounded-full bg-biscayne flex items-center justify-center flex-shrink-0">
                <LogoSvg h={16} variant="light" />
              </div>
              <div
                className="ios-glass mt-1 flex items-center rounded-[100px]"
                style={{
                  // Pills blur at 12, not the class default of 20: they are
                  // small surfaces and the heavier blur flattens the thread
                  // showing through them.
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  padding: '4px 10px',
                }}
              >
                <span className="text-[13px] leading-[18px] font-semibold text-black">Double Bagel</span>
              </div>
            </div>
          );
        }

        return (() => {
          const isCoach = effectiveUid && lessons.some(l => l.coachId === effectiveUid);
          const otherId = getOtherParticipantId();
          // Read off the OTHER user's profile.role rather than off lesson
          // records: a conversation with a coach is tappable before any lesson
          // exists, and the Inbox row uses the same canonical source.
          const otherIsCoach = otherUserProfile?.role === 'coach';
          const opensCoachProfile = otherIsCoach && !!otherId;
          const opensPlayerPeek = !!isCoach && !!otherId && !otherIsCoach;
          const tappable = opensCoachProfile || opensPlayerPeek;

          const openProfile = () => {
            if (opensCoachProfile) {
              navigate(coachProfilePath(otherId!));
            } else if (opensPlayerPeek) {
              setPlayerProfileModal({
                playerId: otherId!,
                seedName: getOtherParticipantName(),
                seedAvatarUrl: getOtherParticipantDetails()?.avatarUrl,
              });
            }
          };

          return (
            /* Floats over the message area: no bar, no border, no fill. The
               lesson CTA is absolutely placed at the right so it does not push
               the centred stack off centre. */
            <div className="absolute inset-x-0 top-0 z-10 flex flex-col items-center" style={{ paddingTop: 12, paddingBottom: 12 }}>
              {/* Tappable only when there is somewhere to go. The chevron is
                  rendered on the same condition, so it never promises a
                  destination that does not exist. */}
              <div
                role={tappable ? 'button' : undefined}
                tabIndex={tappable ? 0 : undefined}
                aria-label={tappable ? `View ${getOtherParticipantName()}'s profile` : undefined}
                className={`flex flex-col items-center rounded-[8px] ${
                  tappable
                    ? 'ios-press cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40'
                    : ''
                }`}
                onClick={tappable ? openProfile : undefined}
                onKeyDown={tappable ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openProfile(); }
                } : undefined}
              >
                {/* The thread header's 36 (components.md 4). */}
                <Avatar url={getOtherParticipantDetails()?.avatarUrl} name={getOtherParticipantName()} size={36} />
                {/* Translucent pill, not bare text: this block floats over
                    scrolling content, which is the one case where a material is
                    the right answer rather than a fill. See DESIGN_SYSTEM.md
                    MT-1. */}
                <div
                  className="ios-glass mt-1 flex items-center gap-1 rounded-[100px]"
                  style={{
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    padding: '4px 10px',
                  }}
                >
                  <h3 className="text-[13px] leading-[18px] font-semibold text-black">
                    {getOtherParticipantName()}
                  </h3>
                  {tappable && (
                    <svg width="8" height="14" viewBox="0 0 8 14" fill="none" stroke="rgba(60,60,67,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" aria-hidden="true"><path d="M1 1L7 7L1 13" /></svg>
                  )}
                </div>
              </div>
              {/* Decision 108: the next call, when one is booked, top right; no video-call button, no
                  three-dot menu. MT-1 glass: the message list scrolls beneath it. It opens the lesson. */}
              {nextLesson && (
                <div className="absolute right-3 top-3 flex items-center md:right-8 md:top-1/2 md:-translate-y-1/2">
                  <button type="button" data-next-call
                    // Decision 135: Lessons with that lesson selected, the list beside it; a phone keeps its page.
                    onClick={() => {
                      if (window.matchMedia('(max-width: 767px)').matches) { navigate(`/live/${nextLesson.id}`); return; }
                      const t = lessonsTarget({ isCoach: nextLesson.coachId === currentUser?.uid, bookingId: nextLesson.id, delivered: false });
                      navigate(t.to, { state: t.state });
                    }}
                    className="ios-glass ios-press flex min-h-9 flex-col items-start justify-center rounded-[100px] px-2.5 py-1 text-left text-[11px] leading-[13px] font-semibold text-db-text-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 md:h-9 md:flex-row md:items-center md:py-0 md:text-[13px] md:leading-[18px]"
                    style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
                    {/* On a phone it stacks, so it clears the centred photo (walk of 29 Oct); one line on a wide screen. */}
                    <span>Next call</span><span className="hidden whitespace-pre md:inline"> · </span><span>{whenLine(nextLesson.scheduledStartAt, viewerZone)}</span>
                  </button>
                </div>
              )}
            </div>
          );
        })();
      })()}

      {/* Focus mode. Sits inside the pane, so the sidebar is untouched, and
          under the header and composer at z-10, so neither is blurred. Its box
          is the message list's box: the list is the only in-flow child of this
          column, and the header and composer are absolute overlays on top of
          it. */}
      {focusedThread && (
        <div className="absolute inset-0 z-[5]">
          <div
            className="absolute inset-0"
            onClick={closeThread}
            style={{
              background: 'rgba(255, 255, 255, 0.72)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          />
          {/* pointer-events-none so a click on the empty space around the
              thread falls through to the scrim and exits. The thread itself
              takes them back. */}
          <div
            className="relative h-full flex flex-col justify-center overflow-y-auto pointer-events-none px-4"
            style={{ paddingTop: hasFloatingHeader ? FLOATING_HEADER_H : 16, paddingBottom: 60 }}
          >
            <div className="pointer-events-auto flex flex-col gap-2">
              {[focusedThread.parent, ...focusedThread.replies].map(m => (
                <React.Fragment key={m.id}>
                  {renderMessageBubble(m, {
                    showAvatar: true,
                    messageLesson: m.lessonId ? lessons.find(l => l.id === m.lessonId) ?? null : null,
                    messageOrder: null,
                    hideReplyChrome: true,
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesScrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 flex flex-col gap-2 bg-db-bg"
        onTouchStart={handleRevealTouchStart}
        onTouchMove={handleRevealTouchMove}
        onTouchEnd={handleRevealTouchEnd}
        onTouchCancel={handleRevealTouchEnd}
        style={{
          // Vertical panning belongs to the browser; this handler only ever
          // claims horizontal movement. Unconditional — the reveal works with
          // or without the floating header.
          touchAction: 'pan-y',
          // Clearing the composer has nothing to do with the header, but this
          // used to sit inside the hasFloatingHeader branch — so on mobile,
          // where Inbox passes hideHeader, the list had NO bottom padding and
          // the last message could never scroll clear of the composer.
          // `keyboardInset` keeps that true while the keyboard is open.
          paddingBottom: COMPOSER_CLEARANCE + keyboardInset,
          paddingTop: hasFloatingHeader ? FLOATING_HEADER_H : 16,
          ...(hasFloatingHeader ? {
          // Fades content out as it passes under the floating header. A mask
          // paints alpha only — it does not affect layout, scrollTop, or the
          // header, which is a sibling outside this element and so unmasked.
          //
          // Nothing shows behind the photo and the name pill: the list is
          // fully transparent down to the pill's foot (12 + 40 + 4 + 30 = 86)
          // and fades in over the header's last 10. A fade across the whole
          // 96 left a lesson card scrolled up under the header legible
          // behind the photo at 390, where it read as a card pinned under it
          // (staging walk 2026-10-25, finding 10).
          maskImage: `linear-gradient(to bottom, transparent 0, transparent ${HEADER_IDENTITY_H}px, #000 ${FLOATING_HEADER_H}px)`,
          WebkitMaskImage: `linear-gradient(to bottom, transparent 0, transparent ${HEADER_IDENTITY_H}px, #000 ${FLOATING_HEADER_H}px)`,
          } : {}),
        }}
      >
        {messages.length === 0 ? (
          /* Local rather than the shared EmptyState: that component draws a
             tinted circle behind its icon and is used by Lessons, Orders and
             the Inbox list, so restyling it here would change those too. */
          <div className="my-auto flex flex-col items-center px-8 text-center">
            <svg
              width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="rgba(60,60,67,0.3)" strokeWidth={1.75}
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h3 className="mt-3 text-[17px] leading-[22px] font-semibold text-[#1C1C1C]">
              No messages yet
            </h3>
            <p className="mt-1 text-[15px] leading-[20px] text-[rgba(60,60,67,0.6)]">
              Send a message to start the conversation.
            </p>
          </div>
        ) : (
          <div
            className="flex flex-col gap-2"
            style={{
              // One transform for the whole thread, not one per row: the
              // timestamps are absolutely placed off the right edge of each
              // row, so moving the layer brings them all into view together.
              transform: revealX ? `translateX(${-revealX}px)` : undefined,
              transition: dragging ? 'none' : 'transform 320ms cubic-bezier(0.32, 0.72, 0, 1)',
            }}
          >
            {/* iMessage-style session grouping: a centered, muted timestamp
                divider opens each group, where a "group" is a run of messages
                with < 60min gaps on the same calendar day. Per-bubble inline
                timestamps were removed in favor of this divider plus the
                hover-reveal label inside MessageBubble. */}
            {groupMessagesIntoSessions(messages).map((session, sessionIdx) => (
              <React.Fragment key={`session-${sessionIdx}-${session.messages[0]?.id ?? ''}`}>
                <div className="flex justify-center my-4 first:mt-0">
                  <span className="text-xs font-medium text-db-text-3 tracking-wide">
                    {session.header}
                  </span>
                </div>
                {session.messages.map((message, indexInSession, sessionMessages) => {
                  const messageLesson = message.lessonId
                    ? lessons.find(l => l.id === message.lessonId)
                    : null;
                  // Resolve the lesson's parent order so MessageBubble can
                  // label the system-event card by offering type. Falls back
                  // to undefined if orders haven't loaded yet — the bubble
                  // label degrades to a generic copy via the helper.
                  const messageOrder = messageLesson
                    ? conversationOrders.find(o => o.id === messageLesson.orderId) ?? null
                    : null;

                  // Show avatar only on the last message in a same-sender streak
                  // within this session. Across session boundaries the next
                  // session's first message gets its own avatar anyway.
                  const next = sessionMessages[indexInSession + 1];
                  const isLastInStreak = !next || next.senderId !== message.senderId;

                  // 2px between consecutive messages from one sender, 8 when
                  // the sender changes. The list's own gap-2 supplies the 8;
                  // a negative top margin closes it back to 2 within a streak.
                  const prev = sessionMessages[indexInSession - 1];
                  const continuesStreak = !!prev && prev.senderId === message.senderId;

                  return (
                    <div
                      key={message.id || `msg-${sessionIdx}-${indexInSession}`}
                      ref={(el) => {
                        messageRefs.current[message.id] = el;
                      }}
                      className="relative transition-colors duration-500"
                      style={continuesStreak ? { marginTop: -6 } : undefined}
                    >
                      {renderMessageBubble(message, { showAvatar: isLastInStreak, messageLesson: messageLesson ?? null, messageOrder })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            {otherIsTyping && (
              <div className="flex items-center gap-2 px-4 py-2">
                <div className="flex items-center gap-1 bg-white border border-db-border rounded-2xl px-3.5 py-2">
                  <span className="flex gap-0.5">
                    <span className="w-[6px] h-[6px] rounded-full bg-db-text-3 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
                    <span className="w-[6px] h-[6px] rounded-full bg-db-text-3 animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1s' }} />
                    <span className="w-[6px] h-[6px] rounded-full bg-db-text-3 animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1s' }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input — hidden in readOnly mode. Absolutely placed so the
          message list runs beneath it: a material needs something to blur, and
          as a flex sibling there was nothing behind it. Same treatment as the
          coach-name pill, for the same reason (DESIGN_SYSTEM.md MT-1). */}
      {readOnly ? null : <div className="ios-glass absolute inset-x-0 z-10" style={{ bottom: keyboardInset }}>
        <>
            {/* Video Upload Progress */}
            {uploadingVideo && (
              <div className="mb-3 bg-db-navy-light border border-db-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-db-text-1 font-medium">Uploading video...</span>
                  <span className="text-sm text-biscayne">{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-db-border rounded-full h-2">
                  <div
                    className="bg-biscayne h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Message Input — unified composer card. Single container holds
                attach, textarea, and send; container handles border + focus
                glow so the three elements read as one premium chat input.
                `items-center` cross-aligns the textarea text with the icon
                buttons on single-line height; as the textarea auto-grows
                (capped at 120px ≈ 5 lines), the container grows with it
                and everything stays optically centered. Focus ring mirrors
                the existing `.input-glow` convention (biscayne border +
                3px navy@12% shadow). */}
            {/* No bar: the plus and the field float directly on the pane, which
                is already white. Height 8 + 28 + 8 = 44. */}
            {/* The next live lesson with this person stays in view above the field, so a booked
                lesson is never only a row on Lessons; it opens the lesson's page (walk W14). */}
            {nextLesson && chip === 'happened' && (
              <div className="flex gap-2 overflow-x-auto px-4 pb-1 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <button type="button" onClick={() => setHappenedOpen(true)} className={`${BTN_SECONDARY_SM} flex-shrink-0`}
                  data-testid="happened-chip">
                  <Clock size={16} aria-hidden="true" />Did this lesson happen?
                </button>
              </div>
            )}
            {happenedOpen && nextLesson && (
              <Sheet title="Did this lesson happen?" onClose={() => setHappenedOpen(false)} busy={happenedBusy}>
                <div className="flex flex-col gap-3">
                  <button type="button" onClick={() => { void answerHappened(true); }} disabled={happenedBusy}
                    className={`${BTN_PRIMARY} w-full`}>Yes</button>
                  <button type="button" onClick={() => { void answerHappened(false); }} disabled={happenedBusy}
                    className={`${BTN_SECONDARY} w-full`}>No</button>
                </div>
              </Sheet>
            )}
            {/* The next call is the header's now (decision 108); the composer keeps only the Move chip. */}
            {nextLesson && chip === 'move' && (
              <div className="flex gap-2 overflow-x-auto px-4 pb-1 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {/* Move first: inside a day it is the one that cannot wait. */}
                {adjustable && (
                  <button type="button" onClick={() => setAdjustOpen(true)} className={`${BTN_SECONDARY_SM} flex-shrink-0`}
                    data-testid="adjust-chip">
                    <Clock size={16} aria-hidden="true" />Move {timeLabel(nextLesson.scheduledStartAt, viewerZone)} Lesson
                  </button>
                )}
              </div>
            )}
            {adjustOpen && nextLesson && (
              <AdjustTimeSheet title={`Move ${timeLabel(nextLesson.scheduledStartAt, viewerZone)} Lesson`} booking={nextLesson} isCoach={nextLesson.coachId === effectiveUid} tz={viewerZone}
                onClose={() => setAdjustOpen(false)} onPickTime={() => { setAdjustOpen(false); setRequestOpen(true); }} />
            )}
            {/* The coach's move is up to three times, one card the player answers (98, 113; audit 2 Nov, A15). */}
            {requestOpen && nextLesson && nextLesson.coachId === effectiveUid && (
              <CoachRequestTimesOverlay booking={nextLesson} tz={viewerZone}
                playerFirst={firstNameOf(liveOtherId ? currentConversation?.participantDetails?.[liveOtherId]?.name : nextLesson.playerName, 'your player')}
                onClose={() => setRequestOpen(false)} />
            )}
            {requestOpen && nextLesson && liveOtherId && nextLesson.coachId !== effectiveUid && (
              <RequestNewTime title="Request a New Time" booking={nextLesson} isCoach={nextLesson.coachId === effectiveUid} tz={viewerZone}
                otherName={currentConversation?.participantDetails?.[liveOtherId]?.name ?? ''}
                onClose={() => setRequestOpen(false)} />
            )}
            <div
              className="ui-composer flex items-center"
              style={{
                padding: 'var(--composer-pad-y) var(--composer-pad-x)',
                gap: 'var(--composer-gap)',
              }}
              data-testid="composer-row"
            >
              <button
                onClick={handleAttachClick}
                disabled={sending || uploadingVideo}
                className="ios-press flex items-center justify-center rounded-full flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
                style={{
                  width: 'var(--composer-plus)',
                  height: 'var(--composer-plus)',
                  // 8.3's secondary fill, the same as the time card button beside it (audit 41).
                  background: 'rgba(120,120,128,0.16)',
                }}
                title="Attach file"
                aria-label="Attach file"
                data-testid="attach-btn"
              >
                {/* Drawn rather than a Phosphor glyph at a guessed size: the
                    viewBox is the glyph's own 15.3 square, so the rendered
                    size is exactly --composer-plus-glyph and the 2.33 stroke
                    scales with it. Endpoints are inset by half the stroke so
                    the round caps land on the box edge, which is what makes
                    both bars measure the full 15.3 and come out equal. */}
                <svg
                  viewBox="0 0 15.3 15.3"
                  fill="none"
                  stroke="#1C1C1C"
                  strokeWidth="2.33"
                  strokeLinecap="round"
                  aria-hidden="true"
                  data-testid="attach-glyph"
                  style={{ width: 'var(--composer-plus-glyph)', height: 'var(--composer-plus-glyph)' }}
                >
                  <path d="M1.165 7.65H14.135" />
                  <path d="M7.65 1.165V14.135" />
                </svg>
              </button>

              {liveMembership && liveRole && !senderOverride && (
                <button
                  type="button"
                  onClick={() => setTimeCardOpen(true)}
                  className="ios-press flex items-center justify-center rounded-full flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
                  style={{ width: 'var(--composer-plus)', height: 'var(--composer-plus)', background: 'rgba(120,120,128,0.16)' }}
                  title={liveRole === 'coach' ? 'Suggest a Time' : 'Request a Time'}
                  aria-label={liveRole === 'coach' ? 'Suggest a Time' : 'Request a Time'}
                  data-testid="time-card-btn"
                >
                  <CalendarPlus size={18} color="#1C1C1C" aria-hidden="true" />
                </button>
              )}
              {timeCardOpen && liveMembership && liveRole && liveOtherId && (
                <TimeCardComposer membership={liveMembership} role={liveRole} otherId={liveOtherId}
                  otherName={currentConversation?.participantDetails?.[liveOtherId]?.name ?? ''}
                  onClose={() => setTimeCardOpen(false)} />
              )}

              {/* Hidden file input — attach flow unchanged */}
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
              />

              {/* The field owns the border; the send button lives inside it. */}
              <div
                className="flex flex-1 min-w-0 items-center"
                style={{
                  borderRadius: 'var(--composer-field-r)',
                  background: 'rgba(120,120,128,0.06)',
                  border: '1px solid rgba(0,0,0,0.04)',
                  paddingLeft: 'var(--composer-field-pad-l)',
                  paddingRight: 'var(--composer-field-pad-r)',
                  // minHeight, not height: the textarea grows to 120 and the
                  // field has to grow with it.
                  minHeight: 'var(--composer-field-h)',
                }}
                data-testid="composer-field"
              >
                <textarea
                  ref={inputRef}
                  value={messageText}
                  onChange={(e) => {
                    setMessageText(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    if (e.target.value.trim()) emitTyping();
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={focusedId ? "Reply" : "Message"}
                  className="flex-1 min-w-0 bg-transparent border-none outline-none resize-none text-black placeholder:text-[rgba(60,60,67,0.3)]"
                  rows={1}
                  /* Size from .ui-composer: 16 on touch so iOS Safari does not
                     zoom on focus, 13 on pointer devices. */
                  style={{ fontSize: 'var(--composer-text)', lineHeight: 'var(--composer-leading)', minHeight: 'var(--composer-leading)', maxHeight: 120, paddingTop: 4, paddingBottom: 4 }}
                  disabled={sending || uploadingVideo}
                />
                {/* Absent while the field is empty, so the field runs full
                    width, and scaling in on the first character. Still hidden
                    on pointer devices, where Enter sends and a button is
                    redundant. `iosMenuIn` is the scale-from-nothing keyframe
                    already in index.css; reused here rather than adding a
                    second one that does the same thing. */}
                {messageText.trim() && (
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || uploadingVideo}
                    className="ios-press flex items-center justify-center flex-shrink-0 bg-biscayne text-white disabled:opacity-50 [@media(hover:hover)_and_(pointer:fine)]:hidden"
                    style={{
                      width: 'var(--composer-send-w)',
                      height: 'var(--composer-send-h)',
                      borderRadius: 'var(--composer-send-r)',
                      animation: 'iosMenuIn 160ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                    title="Send message"
                    aria-label="Send message"
                    data-testid="send-btn"
                  >
                    {/* Rounded stem plus a chevron head, in a viewBox that is
                        the glyph's own 14 square. Endpoints inset by half the
                        2.3 stroke so the caps land on the box edge and the
                        glyph measures a true 14 tall. */}
                    <svg
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      data-testid="send-glyph"
                      style={{ width: 'var(--composer-send-glyph)', height: 'var(--composer-send-glyph)' }}
                    >
                      <path d="M7 12.85V1.15" />
                      <path d="M2.5 5.65L7 1.15L11.5 5.65" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
        </>
      </div>}


      {/* Drill attachment picker — coach-only, non-DB conversations */}
      {showDrillPicker && (
        <DrillAttachmentPickerModal
          isOpen={showDrillPickerModal}
          onClose={() => setShowDrillPickerModal(false)}
          coachId={effectiveUid ?? ''}
          onDrillSelected={handleDrillSelected}
          onProVideoSelected={handleProVideoSelected}
          onLocalFile={handleVideoSelect}
        />
      )}

      {/* Upload Next Lesson Modal (training program continuation).
          Target lesson number resolution, in priority order:
            1. `pendingUploadLessonNumber` set by the banner CTA dispatcher
               (covers watch-pending → lazy-create where no reserved exists)
            2. First reserved lesson on the order
            3. Latest delivered + 1
          When a reserved lesson matches the target, we pass its ID so the
          modal updates that doc (reserved → in_progress). When no match,
          the modal creates a new lesson via the lazy-create path. */}
      {showUploadModal && activeOrder && (() => {
        const sorted = [...lessons].filter(l => l.orderId === activeOrder.id).sort((a, b) => (a.lessonNumber ?? 0) - (b.lessonNumber ?? 0));
        const reserved = sorted.find(l => l.status === 'reserved');
        const latestDelivered = sorted.filter(l => l.status === 'delivered').at(-1);
        const totalLessons = activeOrder.totalLessons ?? sorted.length;
        const fallbackFromDelivered = latestDelivered?.lessonNumber
          ? latestDelivered.lessonNumber + 1
          : null;
        const nextLessonNum =
          pendingUploadLessonNumber
          ?? reserved?.lessonNumber
          ?? fallbackFromDelivered
          ?? 1;

        if (nextLessonNum > totalLessons || nextLessonNum < 1) return null;

        const matchingReserved = sorted.find(
          l => l.status === 'reserved' && l.lessonNumber === nextLessonNum,
        );

        const closeModal = () => {
          setShowUploadModal(false);
          setPendingUploadLessonNumber(null);
        };

        return (
          <UploadNextLessonModal
            order={activeOrder}
            nextLessonNum={nextLessonNum}
            onClose={closeModal}
            onUploadStarted={closeModal}
            existingLessonId={matchingReserved?.id}
          />
        );
      })()}

      {/* Video Editor Modal — lazy-loaded; only pulled in when the user actually opens it */}
      {editingVideo && editingVideo.videoUrl && (
        <Suspense fallback={null}>
          <VideoEditorModal
            videoUrl={editingVideo.videoUrl}
            onComplete={handleVideoEditorComplete}
            onCancel={handleVideoEditorCancel}
          />
        </Suspense>
      )}

      {playerProfileModal && (
        <PlayerProfileModal
          playerId={playerProfileModal.playerId}
          seedName={playerProfileModal.seedName}
          seedAvatarUrl={playerProfileModal.seedAvatarUrl}
          onClose={() => setPlayerProfileModal(null)}
        />
      )}

      {/* Video call */}
      {/* Delete confirmation modal */}
      {deletingMessage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setDeletingMessage(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-1">
              <h3 className="text-[16px] font-semibold text-db-text-1">Delete message?</h3>
              <p className="text-[13.5px] text-db-text-2 leading-snug">
                This message will be replaced with "This message was deleted" for all participants. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeletingMessage(null)}
                className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-db-text-1 hover:bg-db-bg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PairBookingsContext.Provider>
  );
}
